import React from 'react';

const ColdReadPrivacy = () => {
  return (
    <div className="min-h-screen bg-white text-gray-800 px-6 py-16">
      <h1 className="text-4xl font-bold mb-6 text-center">Privacy Policy</h1>
      <p className="text-center text-gray-600 mb-4">ColdRead for iOS · Last updated: August 27, 2026</p>
      <hr className="my-6" />

      <div className="max-w-3xl mx-auto space-y-6 text-justify leading-relaxed">
        <p>
          ColdRead is a rehearsal app for actors. This policy explains what information the app handles, where it is stored and who can see it. The short version: your scripts and recordings stay on your device, ColdRead has no user accounts, and we do not sell or share personal data.
        </p>

        <h2 className="text-xl font-semibold mt-8">1. Information stored on your device</h2>
        <p>Everything you create in ColdRead is stored locally on your iPhone:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Scripts, lines, character names and the role you chose</li>
          <li>Audio recordings of lines you record</li>
          <li>Reader voice and rehearsal settings</li>
          <li>The number of scripts you have created (used to apply the free-tier limit)</li>
        </ul>
        <p>We do not operate servers that receive this content. It is never uploaded by ColdRead.</p>

        <h2 className="text-xl font-semibold mt-8">2. Microphone and speech recognition</h2>
        <p>
          ColdRead asks for microphone access to record lines and to listen while you rehearse, and for speech recognition access to transcribe what you say. Transcription is performed by Apple's Speech framework. Depending on your device, language and settings, Apple may process the audio on the device or on Apple's servers under Apple's privacy policy. ColdRead does not receive, store or transmit this audio except for the recordings you explicitly save inside the app.
        </p>
        <p>
          You can revoke both permissions at any time in iOS Settings › Privacy &amp; Security. Without them, recording and hands-free rehearsal will not work.
        </p>

        <h2 className="text-xl font-semibold mt-8">3. Purchases and subscriptions</h2>
        <p>
          ColdRead Pro is an auto-renewable subscription billed by Apple through your Apple ID. ColdRead never sees your payment details.
        </p>
        <p>
          To verify subscriptions we use RevenueCat, a subscription management service. RevenueCat receives an anonymous, randomly generated app user identifier, purchase receipts and basic device information (such as device model, OS version, app version and country). This data is used solely to confirm which features you are entitled to and to restore purchases on a new device. It is not linked to your name, email address or Apple ID, and it is not used for advertising or tracking.
        </p>

        <h2 className="text-xl font-semibold mt-8">4. Sharing scripts</h2>
        <p>
          When you use Share, ColdRead creates a file containing the script's text, roles and any recordings you attached, and hands it to the iOS share sheet. Where that file goes (AirDrop, Messages, email, cloud storage) is entirely under your control. Only share scripts with people you trust, and only share material you have the right to share.
        </p>

        <h2 className="text-xl font-semibold mt-8">5. Analytics, advertising and tracking</h2>
        <p>
          ColdRead contains no advertising, no analytics SDKs and no tracking. We do not use the advertising identifier (IDFA) and we do not track you across apps or websites.
        </p>

        <h2 className="text-xl font-semibold mt-8">6. Data retention and deletion</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>Deleting a script removes it and its recordings from your device immediately.</li>
          <li>Settings › Delete All Data removes every script, recording and setting.</li>
          <li>Uninstalling the app removes all app data from the device. The free-tier script counter is kept in the iOS Keychain and may persist after reinstalling.</li>
          <li>Subscription records held by Apple and RevenueCat are retained as required to manage your subscription and for accounting purposes. You can request deletion of the anonymous RevenueCat record by contacting us.</li>
        </ul>

        <h2 className="text-xl font-semibold mt-8">7. Children</h2>
        <p>
          ColdRead is not directed at children under 13 and does not knowingly collect personal information from children.
        </p>

        <h2 className="text-xl font-semibold mt-8">8. Your rights</h2>
        <p>
          Because ColdRead does not collect personal data on servers, most privacy rights (access, correction, deletion) are exercised directly on your device as described above. If you are in the EU/EEA, UK, or another region with data-protection laws and have a request about the anonymous subscription data, contact us and we will assist.
        </p>

        <h2 className="text-xl font-semibold mt-8">9. Changes to this policy</h2>
        <p>
          We may update this policy when the app changes. The date at the top shows the latest revision; material changes will also be noted in the app's release notes.
        </p>

        <h2 className="text-xl font-semibold mt-8">10. Contact</h2>
        <p>
          Questions or requests: <a href="mailto:hi@n1inovasyon.com" className="text-blue-600 hover:text-blue-800">hi@n1inovasyon.com</a>
        </p>

        <div className="mt-12 p-4 bg-gray-100 rounded-lg">
          <p className="text-sm text-gray-600 text-center">
            ColdRead is developed by Zahid Esat Ağca. Contact: <a href="mailto:hi@n1inovasyon.com" className="text-blue-600 hover:text-blue-800">hi@n1inovasyon.com</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ColdReadPrivacy;
